import { type TypedDocumentNode } from '@graphql-typed-document-node/core'
import { type UploadFileInfo } from '@progress/kendo-react-upload'
import { useMutation } from '@tanstack/react-query'
import axios from 'axios'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { usePathParams } from '~/apps/ninesegsAnalyzer/routes/paths'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useNotify } from '~/hooks/useNotify'
import { exists } from '~/utils'

import { pathKey } from '../utils'

import { useObserveUploadedFile } from './useObserveUploadedFile'

type Result = {
  generateUploadURL?: {
    url: string
    awaitId?: string | null
  } | null
}
type Variables = {
  surveyId: string
  fileInfo: UploadFileInfo
}

type UseUploadFileParams<TResult extends Result, TVariables> = {
  document: TypedDocumentNode<TResult, TVariables>
  messages: {
    error: string
    uploading: string
    uploaded: string
    logEvent: string
  }
}

export const useUploadFile = <TResult extends Result, TVariables>({
  document,
  messages,
}: UseUploadFileParams<TResult, TVariables>) => {
  const { surveyId } = usePathParams(pathKey)
  const graphQLRequest = useGraphQLRequest()
  const notify = useNotify()
  const { t } = useTranslation()
  const notifyError = React.useCallback(
    (reason: string) =>
      notify({
        content: `${messages.error}\n${reason}`,
        style: 'error',
      }),
    [messages.error, notify],
  )

  const logEvent = useLogEvent()

  const uploadFileMutation = useMutation<
    string | undefined,
    unknown,
    Omit<Variables, 'surveyId'>,
    unknown
  >({
    mutationFn: async ({ fileInfo, ...rest }) => {
      try {
        const result = await graphQLRequest({
          document,
          variables: {
            surveyId,
            filename: fileInfo.name,
            fileExtension: fileInfo.extension,
            ...rest,
          },
        })

        const url = result.generateUploadURL?.url
        if (!exists(url)) {
          notifyError(t('failed_to_retrieve_upload_url'))

          return undefined
        }

        const awaitId = result.generateUploadURL?.awaitId
        if (!exists(awaitId)) {
          notifyError(t('failed_to_retrieve_awaitid'))

          return undefined
        }

        const file = fileInfo.getRawFile?.()
        if (!exists(file)) {
          notifyError(t('could_not_get_file'))

          return undefined
        }

        notify({ content: messages.uploading, style: 'info' })

        const axiosResult = await axios.put(url, file, {
          headers: {
            'Content-Type':
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
        })
        if (axiosResult.status === 200) {
          logEvent(messages.logEvent)
          notify({ content: messages.uploaded, style: 'success' })

          return awaitId
        }

        notifyError(t('upload_failed'))

        return undefined
      } catch {
        // A ClientError's message is the whole response JSON — never show it.
        notifyError(t('an_error_occurred'))

        return undefined
      }
    },
  })
  const awaitId = uploadFileMutation.data

  useObserveUploadedFile(awaitId, uploadFileMutation.reset)

  return React.useMemo(
    () =>
      [
        uploadFileMutation.mutate,
        uploadFileMutation.isPending || exists(awaitId),
      ] as const,
    [awaitId, uploadFileMutation.isPending, uploadFileMutation.mutate],
  )
}
